"use client";
import {NeoButton, NeoDatePicker, NeoTextBox} from "@/components/forms";
import {NeoError, NeoSuccess} from "@/components/shared";
import {NeoSwitchInput} from "@/components/shared/neo-switch/neo-switch-input";
import {driversService} from "@/service/drivers.service";
import {yupResolver} from "@hookform/resolvers/yup";
import {useEffect, useState} from "react";
import {Controller, Resolver, useForm} from "react-hook-form";
import {AiOutlineLoading} from "react-icons/ai";
import * as yup from "yup";

type complianceEditData = {
  licenceNo: string;
  licenceValidityDate: Date | null;
  badgeNo: string;
  badgeExpiryDate: Date | null;
  medicalTestExpiryDate: Date | null;
  pvcExpiryDate: Date | null;
  ddtCertificateNo: string;
  ddtExpiryDate: Date | null;
  bgv: boolean;
  bgvExpiryDate: Date | null;
};

export const ComplianceForm: React.FC<any> = function ({
  driverComplianceEditdata,
  driverEditdata,
  handleNext
}) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [succssMessage, setSuccessMessage] = useState<string | null>(null);

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const validationSchema = yup.object().shape({
    licenceNo: yup.string(),
    licenceValidityDate: yup
      .date()
      .nullable()
      .typeError("Enter the Valid Date")
      .min(todayStart, "License Expiry Date must be a future date")
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      ),
    badgeNo: yup.string(),
    badgeExpiryDate: yup
      .date()
      .nullable()
      .typeError("Enter the Valid Date")
      .min(todayStart, "Badge Expiry Date must be a future date")
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      ),
    medicalTestExpiryDate: yup
      .date()
      .nullable()
      .typeError("Enter the Valid Date")
      .min(todayStart, "Medical Test Expiry Date must be a future date")
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      ),
    pvcExpiryDate: yup
      .date()
      .nullable()
      .typeError("Enter the Valid Date")
      .min(todayStart, "PVC Expiry Date must be a future date")
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      ),
    ddtCertificateNo: yup.string(),
    ddtExpiryDate: yup
      .date()
      .nullable()
      .typeError("Enter the Valid Date")
      .min(todayStart, "DDT Expiry Date must be a future date")
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      ),
    bgv: yup.boolean(),
    bgvExpiryDate: yup
      .date()
      .nullable()
      .typeError("Enter the Valid Date")
      .min(todayStart, "BGV Expiry Date must be a future date")
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      )
  });

  const {
    handleSubmit,
    formState: {isSubmitting, errors},
    control,
    setError,
    setValue,
    reset,
    clearErrors,
    register
  } = useForm<complianceEditData>({
    mode: "onChange",
    resolver: yupResolver(validationSchema) as Resolver<complianceEditData>
  });

  useEffect(() => {
    if (driverComplianceEditdata) {
      reset(driverComplianceEditdata);
    }
  }, [driverComplianceEditdata]);

  const formatDate = (date: Date | null): string | null => {
    if (!date) return null;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const onDateChange = (name: any, event: any) => {
    clearErrors(name);
    setValue(name, event);
  };

  const onSubmit = async (data: complianceEditData) => {
    setLoading(true);
    const formData = new FormData();
    if (driverEditdata?.driverId) {
      formData.append("userId", driverEditdata.driverId);
    }
    formData.append("name", driverEditdata?.name ?? "");
    formData.append("phone", driverEditdata?.mobileNo ?? "");
    formData.append("licenseNumber", data.licenceNo || "");
    if (data.licenceValidityDate) {
      formData.append(
        "licenseValidityDate",
        formatDate(data.licenceValidityDate) as string
      );
    }
    if (driverEditdata?.dateOfBirth) {
      formData.append("dob", formatDate(driverEditdata.dateOfBirth) as string);
    }
    formData.append("instituteId", driverEditdata?.institutionId ?? "");
    formData.append("loginUserId", driverEditdata?.userId ?? "");
    formData.append("badgeNo", data.badgeNo || "");
    formData.append("ddtCertificateNo", data.ddtCertificateNo || "");
    formData.append("bgv", String(!!data.bgv));
    if (data.badgeExpiryDate) {
      formData.append(
        "badgeExpiryDate",
        formatDate(data.badgeExpiryDate) as string
      );
    }
    if (data.medicalTestExpiryDate) {
      formData.append(
        "medicalTestExpiryDate",
        formatDate(data.medicalTestExpiryDate) as string
      );
    }
    if (data.pvcExpiryDate) {
      formData.append(
        "pvcExpiryDate",
        formatDate(data.pvcExpiryDate) as string
      );
    }
    if (data.ddtExpiryDate) {
      formData.append(
        "ddtExpiryDate",
        formatDate(data.ddtExpiryDate) as string
      );
    }
    if (data.bgvExpiryDate) {
      formData.append(
        "bgvExpiryDate",
        formatDate(data.bgvExpiryDate) as string
      );
    }

    const request = driverEditdata?.driverId
      ? driversService.updateDriverdata(formData)
      : driversService.addDriverdata(formData);

    await request
      .then((response: any) => {
        if (response?.data?.status === "success") {
          setSuccessMessage(
            response?.data?.message || "Compliance details saved successfully"
          );
          handleNext?.();
        } else {
          setErrorMessage(
            response?.data?.message
              ? response?.data?.message
              : "Sorry!, We have technical issue please try again later"
          );
        }
      })
      .catch(err => {
        if (err?.errors?.length > 0) {
          err?.errors.forEach(
            ({field, message}: {field: any; message: string}) => {
              setError(field, {type: "server", message: message});
            }
          );
        } else {
          setErrorMessage(
            err?.message ||
              "Sorry!, We have technical issue please try again later"
          );
        }
      })
      .finally(() => {
        setLoading(false);
        setTimeout(() => {
          setErrorMessage(null);
          setSuccessMessage(null);
        }, 2000);
      });
  };

  return (
    <div className="w-full rounded-lg border border-gray-200 p-1 shadow-md">
      <div className="rouned-lg mb-2 flex flex-col justify-start border-b-2 border-gray-200 bg-white p-4">
        <h2 className="text-lg font-semibold">Compliance Information</h2>
        <p className="text-sm text-gray-500">
          Please fill in the compliance information of the driver.
        </p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full px-5">
        <div className="mb-2 grid w-full grid-cols-2 gap-2 max-sm:grid-cols-1">
          <Controller
            name="licenceNo"
            control={control}
            render={({field}) => (
              <NeoTextBox
                {...field}
                label="License Number"
                id="licenceNo"
                placeholder="Enter license number"
                color={errors.licenceNo ? "danger" : "default"}
                isInvalid={errors.licenceNo ? true : false}
                errorMessage={errors.licenceNo && `${errors.licenceNo.message}`}
                {...register("licenceNo")}
              />
            )}
          />
          <Controller
            name="licenceValidityDate"
            control={control}
            render={({field: {ref, value, onChange, ...fieldRest}}) => (
              <NeoDatePicker
                {...fieldRest}
                minDate={todayStart}
                label="License Expiry Date"
                placeholder="license expiry date"
                errorMessage={
                  errors.licenceValidityDate &&
                  `${errors.licenceValidityDate.message}`
                }
                value={value}
                handleDateChange={(e: any) => {
                  onChange(e);
                  onDateChange("licenceValidityDate", e);
                }}
              />
            )}
          />
        </div>

        <div className="mb-2 grid w-full grid-cols-2 gap-2 max-sm:grid-cols-1">
          <Controller
            name="badgeNo"
            control={control}
            render={({field}) => (
              <NeoTextBox
                {...field}
                label="Badge No"
                id="badgeNo"
                placeholder="Enter badge number"
                color={errors.badgeNo ? "danger" : "default"}
                isInvalid={errors.badgeNo ? true : false}
                errorMessage={errors.badgeNo && `${errors.badgeNo.message}`}
                {...register("badgeNo")}
              />
            )}
          />
          <Controller
            name="badgeExpiryDate"
            control={control}
            render={({field: {ref, value, onChange, ...fieldRest}}) => (
              <NeoDatePicker
                {...fieldRest}
                minDate={todayStart}
                label="Badge Expiry Date"
                placeholder="badge expiry date"
                errorMessage={
                  errors.badgeExpiryDate && `${errors.badgeExpiryDate.message}`
                }
                value={value}
                handleDateChange={(e: any) => {
                  onChange(e);
                  onDateChange("badgeExpiryDate", e);
                }}
              />
            )}
          />
        </div>

        <div className="mb-2 grid w-full grid-cols-2 gap-2 max-sm:grid-cols-1">
          <Controller
            name="medicalTestExpiryDate"
            control={control}
            render={({field: {ref, value, onChange, ...fieldRest}}) => (
              <NeoDatePicker
                {...fieldRest}
                minDate={todayStart}
                label="Medical Test Expiry Date"
                placeholder="medical test expiry date"
                errorMessage={
                  errors.medicalTestExpiryDate &&
                  `${errors.medicalTestExpiryDate.message}`
                }
                value={value}
                handleDateChange={(e: any) => {
                  onChange(e);
                  onDateChange("medicalTestExpiryDate", e);
                }}
              />
            )}
          />
          <Controller
            name="pvcExpiryDate"
            control={control}
            render={({field: {ref, value, onChange, ...fieldRest}}) => (
              <NeoDatePicker
                {...fieldRest}
                minDate={todayStart}
                label="PVC Expiry Date"
                placeholder="pvc expiry date"
                errorMessage={
                  errors.pvcExpiryDate && `${errors.pvcExpiryDate.message}`
                }
                value={value}
                handleDateChange={(e: any) => {
                  onChange(e);
                  onDateChange("pvcExpiryDate", e);
                }}
              />
            )}
          />
        </div>

        <div className="mb-2 grid w-full grid-cols-2 gap-2 max-sm:grid-cols-1">
          <Controller
            name="ddtCertificateNo"
            control={control}
            render={({field}) => (
              <NeoTextBox
                {...field}
                label="DDT Certificate No"
                id="ddtCertificateNo"
                placeholder="Enter DDT certificate number"
                color={errors.ddtCertificateNo ? "danger" : "default"}
                isInvalid={errors.ddtCertificateNo ? true : false}
                errorMessage={
                  errors.ddtCertificateNo &&
                  `${errors.ddtCertificateNo.message}`
                }
                {...register("ddtCertificateNo")}
              />
            )}
          />
          <Controller
            name="ddtExpiryDate"
            control={control}
            render={({field: {ref, value, onChange, ...fieldRest}}) => (
              <NeoDatePicker
                {...fieldRest}
                minDate={todayStart}
                label="DDT Expiry Date"
                placeholder="ddt expiry date"
                errorMessage={
                  errors.ddtExpiryDate && `${errors.ddtExpiryDate.message}`
                }
                value={value}
                handleDateChange={(e: any) => {
                  onChange(e);
                  onDateChange("ddtExpiryDate", e);
                }}
              />
            )}
          />
        </div>

        <div className="mb-2 grid w-full grid-cols-2 gap-2 max-sm:grid-cols-1">
          <Controller
            name="bgv"
            control={control}
            render={({field: {value}}) => (
              <NeoSwitchInput
                defaultSelected={value}
                isSelected={value}
                className="!mt-0"
                label="BGV Status"
                activeLabel="Completed"
                inActiveLabel="Not Completed"
                onValueChange={(val: boolean) => {
                  clearErrors("bgv");
                  setValue("bgv", val);
                }}
              />
            )}
          />
          <Controller
            name="bgvExpiryDate"
            control={control}
            render={({field: {ref, value, onChange, ...fieldRest}}) => (
              <NeoDatePicker
                {...fieldRest}
                minDate={todayStart}
                label="BGV Expiry Date"
                placeholder="bgv expiry date"
                errorMessage={
                  errors.bgvExpiryDate && `${errors.bgvExpiryDate.message}`
                }
                value={value}
                handleDateChange={(e: any) => {
                  onChange(e);
                  onDateChange("bgvExpiryDate", e);
                }}
              />
            )}
          />
        </div>

        <div className="flex flex-col items-center justify-end p-4">
          <NeoButton
            className="mx-2 mt-2"
            type="submit"
            size="sm"
            disabled={isSubmitting}
            isLoading={loading}
            spinner={
              <AiOutlineLoading size="xs" className="animate-spin h-6 w-6" />
            }
            color="black"
          >
            Submit
          </NeoButton>
          {errorMessage ? <NeoError message={errorMessage}></NeoError> : null}
          {succssMessage ? (
            <NeoSuccess message={succssMessage}></NeoSuccess>
          ) : null}
        </div>
      </form>
    </div>
  );
};
